import { createContext, useContext } from 'react';
import type { Bundle } from '../../lib/validate';

/** The draft bundle, so pickers (items, modules, loot tables, zombies) offer what exists in the draft right now. */
export const ContentRefsContext = createContext<Bundle>({});

/** The whole draft bundle (for actions that look across collections, like "Create trigger + mission"). */
export function useContentBundle() {
  return useContext(ContentRefsContext);
}

export function useContentRefs() {
  const b = useContext(ContentRefsContext);
  const present = <T>(list: (T | null | undefined)[] | null | undefined) =>
    (list ?? []).filter((x): x is T => x != null);
  return {
    items: b.items ?? [],
    lootTables: b.lootTables ?? [],
    zombies: b.zombies ?? [],
    winds: present(b.winds),
    baseModules: b.baseModules ?? [],
    categories: b.categories ?? [],
    bosses: present(b.bosses),
    triggers: present(b.triggers),
    missions: present(b.missions),
    cosmetics: present(b.cosmetics),
    encounterTypes: present(b.encounters?.types),
    worldEventTypes: present(b.worldEvents?.types),
  };
}

export type Option = { value: string; label: string };

/** Item options from the draft, labelled "Name (id) · category". `filter` narrows by category. */
export function useItemOptions(filter?: (category: string) => boolean): Option[] {
  const { items } = useContentRefs();
  return items
    .filter((i) => !filter || filter(i.category ?? ''))
    .map((i) => ({
      value: i.id ?? '',
      label: `${i.name || i.id} (${i.id}) · ${i.category ?? ''}`,
    }));
}
